/**
 * 2026-10-28 (owner) · which optional parts of a step this viewer sees — "jo fields use nahi hote, unko
 * hide kar sake". Every one is shown until switched off; off hides it on every step, in the step cards
 * and in the right panel. Per viewer, in this browser (localStorage): it changes what one person sees,
 * never what the record holds or what another person sees.
 *
 * A field the step REQUIRES is never hidden by this (StepForm keeps it): Instruction, May run in, the
 * parameter of a parameter record, the equipment of a dispense or a cleaning step — a save refused for
 * a field nobody can see is a dead end.
 */
import { useSyncExternalStore } from "react";

const KEY = "dz.fields.hidden";
const EVENT = "dz-fields-changed";

export const FIELD_GROUPS = [
  {
    title: "Process hierarchy",
    hint: "Under the processes and their operations.",
    fields: [
      { key: "left.continuous", label: "Continuous monitoring" },
    ],
  },
  {
    title: "Step cards",
    hint: "The tags under each step's name in the list.",
    fields: [
      { key: "card.place", label: "Location (May run in)" },
      { key: "card.equipment", label: "Equipment" },
      { key: "card.performer", label: "Performed by" },
      { key: "card.signer", label: "Checked / verified / witnessed by" },
    ],
  },
  {
    title: "Config data",
    hint: "The optional fields a step is configured with. Required ones always stay.",
    fields: [
      { key: "panel.duration", label: "Step duration" },
      { key: "panel.depends", label: "Depends on" },
      { key: "panel.equipment", label: "Equipment (optional)" },
      { key: "panel.records", label: "What it records" },
      { key: "panel.checklist", label: "Checklist" },
      { key: "panel.parameter", label: "Parameter, its monitoring and CPV" },
      { key: "panel.fields", label: "Fields the operator fills" },
      { key: "panel.attachment", label: "Photo / file attachment" },
    ],
  },
];

const read = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

let snapshot = read();
const listeners = new Set();
const emit = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => { if (e.key === KEY) { snapshot = read(); emit(); } });
  window.addEventListener(EVENT, () => { snapshot = read(); emit(); });
}

export function setFieldShown(key, shown) {
  const next = shown ? snapshot.filter((k) => k !== key) : [...new Set([...snapshot, key])];
  snapshot = next;
  try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* the choice still holds for this visit */ }
  emit();
}

export function showAllFields() {
  snapshot = [];
  try { localStorage.removeItem(KEY); } catch { /* nothing stored to clear */ }
  emit();
}

const subscribe = (l) => { listeners.add(l); return () => listeners.delete(l); };

/** `shown(key)` — true unless this viewer switched it off. */
export function useFieldPrefs() {
  const hidden = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
  return { hidden, shown: (key) => !hidden.includes(key) };
}
